import type { QueryKey } from '$lib/api/tanstack-query';

const BASE = ['machine'] as const;
const LIST_BASE = [...BASE, 'list'] as const;
const SUMMARIES_BASE = [...LIST_BASE, 'summary'] as const;

export const machineKeys = {
  get: (machineID: string | undefined): QueryKey => [...BASE, { machineID }],
  parts: (machineID: string | undefined): QueryKey => [
    ...machineKeys.get(machineID),
    'parts',
  ],
  partConfigs: (machineID: string | undefined): QueryKey => [
    ...machineKeys.parts(machineID),
    'configs',
  ],
  partsMetadata: (machineID: string | undefined): QueryKey => [
    ...machineKeys.parts(machineID),
    'metadata',
  ],
  machineMetadata: (machineID: string | undefined): QueryKey => [
    ...machineKeys.get(machineID),
    'metadata',
  ],
  simSandboxes: (machineID: string | undefined): QueryKey => [
    ...machineKeys.get(machineID),
    'simSandboxes',
  ],
  simSandboxStatus: (
    machineID: string | undefined,
    sandboxID: string | undefined
  ): QueryKey => [...machineKeys.simSandboxes(machineID), { sandboxID }],
  simSandboxPrice: (
    machineID: string | undefined,
    partID: string | undefined
  ): QueryKey => [...machineKeys.get(machineID), 'simSandboxPrice', { partID }],
  list: (locationID: string | undefined): QueryKey => [
    ...LIST_BASE,
    { locationID },
  ],
  summaries: (organizationID: string | undefined): QueryKey => [
    ...SUMMARIES_BASE,
    { organizationID },
  ],
  organizationSummary: (organizationID: string | undefined): QueryKey => [
    ...SUMMARIES_BASE,
    'organizationSummary',
    { organizationID },
  ],
};

export interface KeyFilters {
  /** Keys to invalidate for a specific machine. */
  machine?: {
    id: string | undefined;
    parts?: true;
    partsMetadata?: true;
    machineMetadata?: true;
  };
  /** Keys to invalidate for machine lists */
  list?: ListFilter;
}

export enum ListFilter {
  ALL = 'all',
  SUMMARIES = 'summaries',
}

/**
 * Get a list of relevant machine query keys.
 *
 * Useful for query invalidation and removal.
 */
export const getMachineQueryKeys = ({
  machine,
  list,
}: KeyFilters): readonly QueryKey[] => {
  const queryKeys = [];

  if (machine?.parts) {
    queryKeys.push(
      machineKeys.parts(machine.id),
      machineKeys.partsMetadata(machine.id)
    );
  } else if (machine?.partsMetadata) {
    queryKeys.push(machineKeys.partsMetadata(machine.id));
  } else if (machine?.machineMetadata) {
    queryKeys.push(machineKeys.machineMetadata(machine.id));
  } else if (machine) {
    queryKeys.push(machineKeys.get(machine.id));
  }

  if (list === ListFilter.ALL) {
    queryKeys.push(LIST_BASE);
  } else if (list === ListFilter.SUMMARIES) {
    queryKeys.push(SUMMARIES_BASE);
  }

  return queryKeys;
};
